import { applySnapshot, getSnapshot, types } from 'mobx-state-tree';
import { AppealStatuses, AppealTypes, AppealSources } from '../constants';

export const FiltersModel = types
  .model('Filters', {
    offset: types.optional(types.number, 0),
    limit: types.optional(types.number, 20),
    deadline_at__lt: types.optional(types.maybeNull(types.string), null),
    status__in: types.optional(
      types.array(types.enumeration(Object.values(AppealStatuses))),
      []
    ),
    source__in: types.optional(
      types.array(types.enumeration(Object.values(AppealSources))),
      []
    ),
    category_ids__in: types.optional(types.array(types.string), []),
    number__like: types.optional(types.string, ''),
    number__in: types.optional(types.array(types.string), []),
    incoming_data__number__like: types.optional(types.string, ''),
    type__in: types.optional(
      types.array(types.enumeration(Object.values(AppealTypes))),
      []
    ),
    created_at__gte: types.optional(types.maybeNull(types.string), null),
    created_at__lt: types.optional(types.maybeNull(types.string), null),
    house___id__in: types.optional(types.array(types.string), []),
    area___id__in: types.optional(types.array(types.string), []),
    requester__phone_numbers__number__in: types.optional(types.string, ''),
    requester__email__in: types.optional(types.string, ''),
    requester___id__in: types.optional(types.array(types.string), []),
    executor__department___id__in: types.optional(
      types.array(types.string),
      []
    ),
    executor__position___id__in: types.optional(types.array(types.string), []),
    executor___id__in: types.optional(types.array(types.string), []),
    observer__department__id__in: types.optional(types.array(types.string), []),
    observer__position__id__in: types.optional(types.array(types.string), []),
    observer__id__in: types.optional(types.array(types.string), []),
    our_spb__problem_number__in: types.optional(types.string, ''),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get activeFilterCount(): number {
      const excludedFields = ['limit', 'offset'];
      let count = 0;

      Object.entries(this.snapshot).forEach(([key, value]) => {
        if (excludedFields.includes(key)) {
          return;
        }

        if (Array.isArray(value) && value.length > 0) {
          count += 1;
        } else if (typeof value === 'string' && value.trim() !== '') {
          count += 1;
        } else if (typeof value === 'boolean') {
          count += 1;
        }
      });

      return count;
    },
    get backendPayload(): Partial<typeof this.snapshot> {
      const payload: Partial<typeof this.snapshot> = {};

      // Если выбран фильтр "Просроченные"
      // отправляем текущую дату + статусы "new" и "run"
      if (self.deadline_at__lt) {
        payload.status__in = Array.from(
          new Set([...self.status__in, 'new', 'run'])
        ) as typeof self.status__in;
      }

      const backendPayload: Partial<typeof this.snapshot> = {
        ...this.snapshot,
        ...payload,
      };

      // Номер проблемы отправляем только для "Наш Санкт-Петербург"
      // или когда способ подачи не выбран.
      const sourceIn = backendPayload.source__in;

      if (sourceIn && sourceIn.length > 0 && !sourceIn.includes('our_spb')) {
        delete backendPayload.our_spb__problem_number__in;
      }

      return backendPayload;
    },
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
    updateArrayField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: string[]
    ) {
      self[fieldName].replace(value);
    },
    toggleArrayField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: string
    ) {
      if (self[fieldName].includes(value)) {
        self[fieldName].replace(
          self[fieldName].filter((el: string) => el !== value)
        );
      } else {
        self[fieldName].push(value);
      }
    },
    saveToLocalStorage() {
      localStorage.setItem('appealsFilters', JSON.stringify(self));
    },
    restoreFromLocalStorage(): boolean {
      const localFilters = localStorage.getItem('appealsFilters');

      if (localFilters) {
        applySnapshot(self, JSON.parse(localFilters));
        localStorage.removeItem('appealsFilters');
      }

      return !!localFilters;
    },
    restoreFilters(snapshot: typeof self.snapshot) {
      applySnapshot(self, snapshot);
    },
    reset() {
      applySnapshot(self, {});
    },
  }));
